/**
 * ArchitectureDiagram — a block diagram of the reconciliation
 * platform's open-source / framework stack, for the admin Recon Logic
 * page's transparency section.
 *
 * Deliberately shows ONLY open-source technologies and frameworks
 * (React, Express, BullMQ, PostgreSQL, etc.). It intentionally does
 * NOT name any hosting provider, cloud region, or deployment detail —
 * that's operational information, not architecture, and not relevant
 * to a client reviewing how the recon works.
 *
 * Pure SVG, themeable via CSS variables, no external dependencies.
 */

interface Tech {
  name: string;
  note?: string;
}

interface Layer {
  title: string;
  subtitle: string;
  techs: Tech[];
  accent: string;
}

const LAYERS: Layer[] = [
  {
    title: 'Presentation',
    subtitle: 'Operator & reviewer interface',
    accent: 'var(--accent)',
    techs: [
      { name: 'React' },
      { name: 'Vite' },
      { name: 'TanStack Query' },
      { name: 'Radix UI' },
      { name: 'Tailwind CSS' },
      { name: 'Axios' },
    ],
  },
  {
    title: 'API',
    subtitle: 'Request handling, validation, security',
    accent: '#6366f1',
    techs: [
      { name: 'Express' },
      { name: 'Zod', note: 'schema validation' },
      { name: 'Helmet', note: 'security headers' },
      { name: 'Rate limiting' },
      { name: 'Multer', note: 'file upload' },
    ],
  },
  {
    title: 'Processing',
    subtitle: 'Asynchronous recon pipeline',
    accent: '#0ea5e9',
    techs: [
      { name: 'BullMQ', note: 'job queue' },
      { name: 'ExcelJS', note: 'portfolio parsing' },
      { name: 'pdf-lib', note: 'DP statement parsing' },
      { name: 'Recon engine', note: 'net-balance matcher' },
    ],
  },
  {
    title: 'Intelligence',
    subtitle: 'AI extraction (PDF statements only)',
    accent: '#8b5cf6',
    techs: [
      { name: 'Claude', note: 'document extraction & indexing' },
    ],
  },
  {
    title: 'Data',
    subtitle: 'System of record & queue backing',
    accent: '#10b981',
    techs: [
      { name: 'PostgreSQL', note: 'source of truth' },
      { name: 'Redis', note: 'queue & cache' },
    ],
  },
];

export function ArchitectureDiagram() {
  return (
    <div className="my-4 rounded-md border overflow-hidden"
         style={{ borderColor: 'var(--glass-border)', background: 'var(--bg-surface)' }}>
      <div className="px-4 py-3" style={{ borderBottom: '1px solid var(--glass-border)' }}>
        <div className="text-sm font-semibold" style={{ color: 'var(--text-strong)' }}>
          Platform architecture
        </div>
        <div className="text-xs mt-0.5" style={{ color: 'var(--text-200)' }}>
          Open-source frameworks and libraries the reconciliation platform is built on.
          Data flows top to bottom; the AI layer is used only for PDF statement extraction.
        </div>
      </div>

      <div className="p-4 flex flex-col gap-2">
        {LAYERS.map((layer, i) => (
          <div key={layer.title}>
            <div
              className="rounded-md p-3"
              style={{
                background: `color-mix(in srgb, ${layer.accent} 7%, transparent)`,
                border: `1px solid color-mix(in srgb, ${layer.accent} 35%, transparent)`,
              }}
            >
              <div className="flex items-baseline gap-2 mb-2">
                <span className="text-xs font-semibold" style={{ color: 'var(--text-strong)' }}>
                  {layer.title}
                </span>
                <span className="text-[11px]" style={{ color: 'var(--text-200)' }}>
                  {layer.subtitle}
                </span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {layer.techs.map(t => (
                  <span
                    key={t.name}
                    className="inline-flex items-baseline gap-1 px-2 py-1 rounded text-[11px] font-mono"
                    style={{
                      background: 'var(--bg-elevated)',
                      border: `1px solid color-mix(in srgb, ${layer.accent} 30%, transparent)`,
                      color: 'var(--text-strong)',
                    }}
                  >
                    {t.name}
                    {t.note && (
                      <span className="font-sans" style={{ color: 'var(--text-200)' }}>
                        · {t.note}
                      </span>
                    )}
                  </span>
                ))}
              </div>
            </div>
            {i < LAYERS.length - 1 && (
              <div className="flex justify-center py-0.5" aria-hidden>
                <svg width="16" height="14" viewBox="0 0 16 14">
                  <path d="M8 0 L8 9 M4 6 L8 10 L12 6"
                        fill="none"
                        stroke="var(--text-200)"
                        strokeWidth="1.4"
                        strokeLinecap="round"
                        strokeLinejoin="round" />
                </svg>
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="px-4 py-2 text-[11px]"
           style={{ borderTop: '1px solid var(--glass-border)', color: 'var(--text-200)' }}>
        All components are industry-standard open-source technologies.
        PostgreSQL is the system of record; files are stored as immutable
        objects and referenced from the database.
      </div>
    </div>
  );
}
